import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { Separator } from "../components/ui/separator";
import { 
  FileText, Copy, Check, ExternalLink, Phone, MessageSquare, 
  Settings, Zap, BookOpen, Code, Terminal, AlertCircle
} from 'lucide-react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { toast } from 'sonner';

export default function HuskyVoiceDocsPage({ user, logout }) {
  const [copiedSection, setCopiedSection] = useState(null);

  const copyToClipboard = (text, section) => {
    navigator.clipboard.writeText(text);
    setCopiedSection(section);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopiedSection(null), 2000);
  };

  const CopyButton = ({ text, section }) => (
    <Button 
      variant="outline" 
      size="sm" 
      onClick={() => copyToClipboard(text, section)}
      className="ml-2"
    >
      {copiedSection === section ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
    </Button>
  );

  // Workflow configurations
  const workflows = {
    preCall: {
      name: 'Pre Call API Call',
      method: 'GET',
      url: 'https://clm.hearclear.in/api/huskyvoice/voice/greeting?phone={{contact_number}}',
      description: 'Returns personalized greeting based on lead source'
    },
    pricing: {
      name: 'Get Product Price',
      method: 'POST',
      url: 'https://clm.hearclear.in/api/huskyvoice/voice/pricing?brand={{brand}}&query={{query}}',
      body: `{
  "product_name": "{{product_name}}",
  "brand": "{{brand}}",
  "category": "{{category}}"
}`,
      description: 'Returns pricing in Hindi for voice agent'
    },
    clinic: {
      name: 'Get Clinic Location',
      method: 'POST',
      url: 'https://clm.hearclear.in/api/huskyvoice/voice/clinic?city={{city}}',
      body: `{
  "city": "{{city}}"
}`,
      description: 'Returns clinic address and timings'
    },
    discount: {
      name: 'Discount During Call',
      method: 'POST',
      url: 'https://clm.hearclear.in/api/huskyvoice/voice/discount?current_price={{price}}',
      body: `{
  "current_price": "{{price}}"
}`,
      description: 'Returns discount information'
    },
    postCall: {
      name: 'Post Call Appointment Booking',
      method: 'POST',
      url: 'https://clm.hearclear.in/api/huskyvoice/voice/book?phone={{contact_number}}',
      body: `{
  "phone": "{{contact_number}}",
  "customer_name": "{{contact_name}}",
  "city": "{{city}}",
  "appointment_date": "{{appointment_date}}",
  "appointment_time": "{{appointment_time}}"
}`,
      description: 'Books appointment and updates CRM'
    }
  };

  // WhatsApp Trigger workflows - Priya can send contextual messages during calls
  const whatsappWorkflows = {
    sendClinic: {
      name: 'Send Clinic Location via WhatsApp',
      method: 'GET',
      url: 'https://clm.hearclear.in/api/huskyvoice/whatsapp/trigger?phone={{contact_number}}&message_type=clinic&city={{city}}',
      description: 'Sends clinic address with Google Maps link to customer via WhatsApp',
      trigger: 'When customer asks "clinic kahan hai" or "address bhejo"'
    },
    sendPricing: {
      name: 'Send Product Pricing via WhatsApp',
      method: 'GET',
      url: 'https://clm.hearclear.in/api/huskyvoice/whatsapp/trigger?phone={{contact_number}}&message_type=pricing&category={{category}}',
      description: 'Sends product pricing details with images to customer',
      trigger: 'When customer asks "price WhatsApp karo" or wants written pricing',
      params: 'category: economy, mid, premium, invisible'
    },
    sendEMI: {
      name: 'Send EMI Information via WhatsApp',
      method: 'GET',
      url: 'https://clm.hearclear.in/api/huskyvoice/whatsapp/trigger?phone={{contact_number}}&message_type=info&info_type=emi',
      description: 'Sends EMI and payment options to customer',
      trigger: 'When customer asks about EMI or payment options'
    },
    sendWarranty: {
      name: 'Send Warranty Details via WhatsApp',
      method: 'GET',
      url: 'https://clm.hearclear.in/api/huskyvoice/whatsapp/trigger?phone={{contact_number}}&message_type=info&info_type=warranty',
      description: 'Sends warranty and service details to customer',
      trigger: 'When customer asks about warranty or service'
    },
    sendFreeTest: {
      name: 'Send Free Test Info via WhatsApp',
      method: 'GET',
      url: 'https://clm.hearclear.in/api/huskyvoice/whatsapp/trigger?phone={{contact_number}}&message_type=info&info_type=free_test',
      description: 'Sends free hearing test information',
      trigger: 'When customer wants details about free hearing test'
    },
    sendBrands: {
      name: 'Send Available Brands via WhatsApp',
      method: 'GET',
      url: 'https://clm.hearclear.in/api/huskyvoice/whatsapp/trigger?phone={{contact_number}}&message_type=info&info_type=brands',
      description: 'Sends list of premium brands available at HearClear',
      trigger: 'When customer asks about brands or quality'
    },
    sendHomeVisit: {
      name: 'Send Home Visit Info via WhatsApp',
      method: 'GET',
      url: 'https://clm.hearclear.in/api/huskyvoice/whatsapp/trigger?phone={{contact_number}}&message_type=info&info_type=home_visit',
      description: 'Sends home visit service details',
      trigger: 'When customer cannot visit clinic or asks for home service'
    },
    sendTrial: {
      name: 'Send Free Trial Info via WhatsApp',
      method: 'GET',
      url: 'https://clm.hearclear.in/api/huskyvoice/whatsapp/trigger?phone={{contact_number}}&message_type=info&info_type=trial',
      description: 'Sends 15-day free trial offer details',
      trigger: 'When customer is hesitant or wants to try before buying'
    },
    sendAppointment: {
      name: 'Send Appointment Confirmation via WhatsApp',
      method: 'GET',
      url: 'https://clm.hearclear.in/api/huskyvoice/whatsapp/trigger?phone={{contact_number}}&message_type=appointment&lead_name={{contact_name}}&appointment_date={{date}}&appointment_time={{time}}&city={{city}}',
      description: 'Sends appointment confirmation with clinic details and map',
      trigger: 'After booking appointment - ALWAYS send this confirmation'
    }
  };

  const mainPrompt = `## AGENT IDENTITY
Tu Priya hai - HearClear India ki AI Hearing Care Advisor.
Gender: Female
Tone: Empathetic, knowledgeable, patient, respectful

## TERA KAAM
1. Leads ko call karna jo hearing test ya hearing aid ke baare mein enquiry kiye hain
2. Unki hearing problem samajhna
3. Hearing loss ke baare mein educate karna
4. Appropriate hearing solution recommend karna
5. Device prices check karna official database se
6. Discount offer karna (maximum 30%)
7. Nearest HearClear clinic mein appointment book karna

## LANGUAGE STYLE
- 70% Hindi + 30% simple English
- Female phrasing use karna (main kar sakti hoon, main batati hoon)
- Simple words use karna
- Elderly patients ke liye slowly aur clearly bolna
- Technical terms avoid karna jab tak zaruri na ho

## HEARCLEAR COMPANY INFO
- India ki trusted hearing healthcare company
- 40+ clinics across India
- Services: Hearing test, hearing aid fitting, trial, programming, after-sales
- Cities: Delhi NCR, Chandigarh, Punjab, Bangalore, Mumbai, Pune, Jaipur, Jammu, Raipur, Amritsar, Mohali
- Website: www.hearclear.in
- Helpline: 9888438843

## HEARING LOSS LEVELS (PTA based)
- Normal: 0-25 dB - Hearing aid ki zarurat nahi
- Mild: 26-40 dB - Entry/mid level device
- Moderate: 41-55 dB - Mid/premium RIC device
- Moderately Severe: 56-70 dB - Premium stronger device
- Severe: 71-90 dB - Power hearing aid
- Profound: 90+ dB - Cochlear implant consultation

## HEARING LOSS TYPES
1. Conductive: Ear wax, infection, fluid se hota hai - medical treatment se theek ho sakta hai
2. Sensorineural: Age, noise exposure se hota hai - hearing aid se help milti hai
3. Mixed: Dono ka combination

## RED FLAGS - PEHLE ENT/DOCTOR RECOMMEND KARO
Agar patient report kare:
- Ear discharge ya bleeding
- Severe ear pain
- Sudden hearing loss
- Recent trauma
- Dizziness/vertigo
- One ear mein sudden major drop

Response: "Is situation mein pehle ENT ya clinic evaluation zaroori rahega. Main direct machine suggest karne ke bajaye pehle proper check-up recommend karungi."

## HEARING AID TYPES
- RIC (Receiver in Canal): Most common, modern
- BTE (Behind the Ear): More powerful
- ITE (In the Ear): Custom molded
- IIC (Invisible in Canal): Very small, discreet

## HEARING AID FEATURES
- Noise reduction
- Directional microphones
- Bluetooth streaming
- Rechargeable battery
- Smartphone app control
- AI speech processing

## BRANDS WE WORK WITH
- Signia (specialist)
- Phonak
- Widex
- Oticon
- Starkey
- ReSound

## SIGNIA DEVICE FAMILIES
- Pure Charge & Go: Rechargeable, Bluetooth
- Styletto: Stylish design, younger users
- Silk: Invisible, custom fit
- Motion: Powerful BTE
- Insio: Custom in-ear
- Signia Active: Earbud style

## DEVICE RECOMMENDATION LOGIC
Mild loss (26-40 dB):
- Entry/mid level, Budget: Under 40k

Moderate loss (41-55 dB):
- Mid to premium, RIC preferred, Budget: 40k-1L

Moderately Severe (56-70 dB):
- Premium device, Stronger RIC/BTE, Budget: 1L-2L

Severe (71-90 dB):
- Power hearing aid, Budget: 1.5L-3L

Profound (90+ dB):
- Cochlear implant consultation, Budget: 3L+

## LIFESTYLE MATCHING
- Low social (mostly home): Basic/mid device
- Moderate (family, outdoor): Mid/premium
- High social (meetings, restaurants): Premium/advanced AI

## PRICE RULES - BAHUT IMPORTANT
1. KABHI price guess mat karo
2. Price API se check karo
3. Agar price nahi mil raha: "Main latest price check karke exact figure batati hoon"
4. Price batate waqt mention karo: model name, single ya pair, listed price

## DISCOUNT POLICY
Maximum discount: 30%
Sequence: Standard price → 5-10% → 15-20% → Max 30%

## QUALIFICATION QUESTIONS
1. "Sunne mein problem kab se hai?"
2. "Kya dono ears mein problem hai ya ek mein?"
3. "Patient ki age kya hai?"
4. "Kya hearing test report available hai?"
5. "Koi ear discharge, pain ya infection hai?"
6. "TV volume zyada rakhna padta hai?"

## CALL FLOW
1. Greeting → 2. Problem samjho → 3. Questions pucho → 4. Red flags check → 5. Educate → 6. Recommend → 7. Price check → 8. Discount → 9. Book appointment → 10. Close

## APPOINTMENT CONVERSION - MAIN GOAL
"Sabse achha ye rahega ki aap ek baar HearClear clinic visit kar lein. Wahan free detailed hearing test hoga, expert consultation milegi, hearing aid trial bhi kar sakte hain. Main aapke liye appointment book kar doon?"

## COCHLEAR IMPLANT CASES
Recommend jab: Profound loss (90+ dB), Hearing aid se bahut kam benefit, Speech clarity bahut poor
"Is level par cochlear implant consultation bhi consider ki ja sakti hai."

## SAMPLE RESPONSES
Price: "Ji, hearing aids 700 rupay se lekar 2.5 lakh tak available hain. Aapki hearing loss aur lifestyle ke hisaab se best option recommend karungi."

Clinic: "Ji, aap konse city mein rehte hain? Main aapko nearest HearClear clinic ka address de deti hoon."

Moderate Loss: "Report ke basis par hearing loss moderate range mein lag raha hai. Is type ke cases mein hearing aid usually achha benefit deta hai."

## SAFETY RULES
- Disease diagnose mat karo
- Medicine prescribe mat karo
- Guarantee mat do
- Clinic evaluation recommend karo
- Helpline: 9888438843
- Max 7 minute call`;

  const closingPrompt = `## CLOSING SCRIPT

### If Appointment Booked:
"Aapka appointment [date] [time] ke liye [clinic name] mein book ho gaya hai.
Clinic ka address hai [address]. Google Maps pe 'HearClear [city]' search kar sakte hain.
Agar purani hearing test report hai toh saath le aayein.
Koi bhi query ho toh 9888438843 pe call kar sakte hain.
HearClear ki taraf se dhanyawad ji. Apna khayal rakhein!"

### If No Appointment Booked:
"Ji, jab bhi convenient ho tab clinic visit kar sakte hain.
HearClear ka helpline number hai 9888438843.
Website pe bhi appointment book kar sakte hain - www.hearclear.in
Dhanyawad ji, apna khayal rakhein!"

### What to Remind:
- Bring old hearing test report if available
- Bring current hearing aid if using one
- Come 10 minutes before appointment time
- Free hearing test at clinic
- No commitment required for trial`;

  const testCommands = `# 1. Test Greeting API
curl "https://clm.hearclear.in/api/huskyvoice/voice/greeting?phone=9876543210"

# 2. Test Pricing API (GET)
curl "https://clm.hearclear.in/api/huskyvoice/voice/pricing"

# 3. Test Pricing API (POST)
curl -X POST "https://clm.hearclear.in/api/huskyvoice/voice/pricing" \\
  -H "Content-Type: application/json" \\
  -d '{"brand": "Signia"}'

# 4. Test Clinic API
curl "https://clm.hearclear.in/api/huskyvoice/voice/clinic?city=Delhi"

# 5. Test Discount API
curl "https://clm.hearclear.in/api/huskyvoice/voice/discount?current_price=25000"`;

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="p-6 space-y-6" data-testid="huskyvoice-docs-page">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-3">
              <FileText className="w-8 h-8 text-teal-600" />
              HuskyVoice Configuration Guide
            </h1>
            <p className="text-slate-600 mt-1">Complete setup documentation for Priya AI Voice Agent</p>
          </div>
          <Badge variant="outline" className="text-teal-600 border-teal-600">
            v2.0 - March 2026
          </Badge>
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-teal-50 to-cyan-50 border-teal-200">
            <CardContent className="p-4 flex items-center gap-3">
              <Settings className="w-8 h-8 text-teal-600" />
              <div>
                <p className="font-semibold text-teal-800">5 Workflows</p>
                <p className="text-sm text-teal-600">Pre, During, Post Call</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
            <CardContent className="p-4 flex items-center gap-3">
              <Phone className="w-8 h-8 text-blue-600" />
              <div>
                <p className="font-semibold text-blue-800">2 Agents</p>
                <p className="text-sm text-blue-600">Inbound & Outbound</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
            <CardContent className="p-4 flex items-center gap-3">
              <MessageSquare className="w-8 h-8 text-purple-600" />
              <div>
                <p className="font-semibold text-purple-800">Hindi Prompts</p>
                <p className="text-sm text-purple-600">Ready to copy</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-orange-50 to-amber-50 border-orange-200">
            <CardContent className="p-4 flex items-center gap-3">
              <Zap className="w-8 h-8 text-orange-600" />
              <div>
                <p className="font-semibold text-orange-800">API Testing</p>
                <p className="text-sm text-orange-600">curl commands</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="workflows" className="space-y-4">
          <TabsList className="grid w-full grid-cols-6 bg-slate-100">
            <TabsTrigger value="workflows" className="flex items-center gap-2">
              <Settings className="w-4 h-4" /> Workflows
            </TabsTrigger>
            <TabsTrigger value="agents" className="flex items-center gap-2">
              <Phone className="w-4 h-4" /> Agent Settings
            </TabsTrigger>
            <TabsTrigger value="prompts" className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4" /> Prompts
            </TabsTrigger>
            <TabsTrigger value="knowledge" className="flex items-center gap-2">
              <BookOpen className="w-4 h-4" /> Knowledge Base
            </TabsTrigger>
            <TabsTrigger value="testing" className="flex items-center gap-2">
              <Terminal className="w-4 h-4" /> API Testing
            </TabsTrigger>
            <TabsTrigger value="troubleshoot" className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4" /> Troubleshoot
            </TabsTrigger>
          </TabsList>

          {/* Workflows Tab */}
          <TabsContent value="workflows" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-teal-600" />
                  Workflow Configurations
                </CardTitle>
                <CardDescription>Copy these settings into HuskyVoice Studio → Integrations → Workflows</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {Object.entries(workflows).map(([key, workflow]) => (
                  <div key={key} className="border rounded-lg p-4 bg-slate-50">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-semibold text-lg text-slate-800">{workflow.name}</h3>
                        <p className="text-sm text-slate-600">{workflow.description}</p>
                      </div>
                      <Badge variant={workflow.method === 'GET' ? 'secondary' : 'default'}>
                        {workflow.method}
                      </Badge>
                    </div>
                    
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium text-slate-700">API URL</label>
                        <div className="flex items-center mt-1">
                          <code className="flex-1 bg-white border rounded px-3 py-2 text-sm font-mono text-slate-700 overflow-x-auto">
                            {workflow.url}
                          </code>
                          <CopyButton text={workflow.url} section={`${key}-url`} />
                        </div>
                      </div>
                      
                      {workflow.body && (
                        <div>
                          <label className="text-sm font-medium text-slate-700">Request Body (JSON)</label>
                          <div className="flex items-start mt-1">
                            <pre className="flex-1 bg-white border rounded px-3 py-2 text-sm font-mono text-slate-700 overflow-x-auto">
                              {workflow.body}
                            </pre>
                            <CopyButton text={workflow.body} section={`${key}-body`} />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* WhatsApp Trigger Workflows */}
            <Card className="border-green-300">
              <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50">
                <CardTitle className="flex items-center gap-2 text-green-800">
                  <MessageSquare className="w-5 h-5" />
                  WhatsApp Trigger APIs
                </CardTitle>
                <CardDescription>Priya can send contextual WhatsApp messages during calls. Configure these as "During-Call" workflows.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {Object.entries(whatsappWorkflows).map(([key, workflow]) => (
                  <div key={key} className="border rounded-lg p-4 bg-white">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className="font-semibold text-lg text-slate-800">{workflow.name}</h3>
                        <p className="text-sm text-slate-600">{workflow.description}</p>
                        {workflow.trigger && (
                          <p className="text-xs text-green-600 mt-1 font-medium">Trigger: {workflow.trigger}</p>
                        )}
                      </div>
                      <Badge variant="secondary" className="bg-green-100 text-green-800">
                        {workflow.method}
                      </Badge>
                    </div>
                    
                    <div className="space-y-3">
                      <div>
                        <label className="text-sm font-medium text-slate-700">API URL</label>
                        <div className="flex items-center mt-1">
                          <code className="flex-1 bg-slate-50 border rounded px-3 py-2 text-sm font-mono text-slate-700 overflow-x-auto">
                            {workflow.url}
                          </code>
                          <CopyButton text={workflow.url} section={`wa-${key}-url`} />
                        </div>
                      </div>
                      {workflow.params && (
                        <div className="text-xs text-slate-500 bg-slate-50 p-2 rounded">
                          Parameters: {workflow.params}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                
                {/* Usage Instructions */}
                <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
                  <h4 className="font-semibold text-green-800 mb-2">How to Configure in HuskyVoice:</h4>
                  <ol className="list-decimal list-inside text-sm text-green-700 space-y-1">
                    <li>Go to HuskyVoice Studio → Agent Settings → Integrations</li>
                    <li>Add these as "During-Call" API workflows</li>
                    <li>Set keyword triggers (e.g., "send clinic", "whatsapp karo")</li>
                    <li>Priya will automatically trigger WhatsApp when customer requests info</li>
                  </ol>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="agents" className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {/* Inbound Agent */}
              <Card>
                <CardHeader className="bg-gradient-to-r from-teal-50 to-cyan-50">
                  <CardTitle className="text-teal-800">Inbound Agent</CardTitle>
                  <CardDescription>HearClear Inbound Leads</CardDescription>
                </CardHeader>
                <CardContent className="p-4 space-y-4">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><span className="text-slate-500">Voice:</span> <span className="font-medium">Priya (Female)</span></div>
                    <div><span className="text-slate-500">WhatsApp:</span> <span className="font-medium">+919811123206</span></div>
                    <div><span className="text-slate-500">Primary Lang:</span> <span className="font-medium">Hindi</span></div>
                    <div><span className="text-slate-500">Max Duration:</span> <span className="font-medium">7 min</span></div>
                    <div><span className="text-slate-500">Speed:</span> <span className="font-medium">0.9x</span></div>
                    <div><span className="text-slate-500">Volume:</span> <span className="font-medium">1.1x</span></div>
                  </div>
                  <Separator />
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-slate-700">Workflow Assignment:</p>
                    <div className="text-sm space-y-1">
                      <div className="flex justify-between"><span className="text-slate-500">Pre-Call:</span> <span>Pre Call API Call</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">During-Call:</span> <span>Get Product Price</span></div>
                      <div className="flex justify-between"><span className="text-slate-500">Post-Call:</span> <span>Post Call Appointment Booking</span></div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Outbound Agent */}
              <Card>
                <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50">
                  <CardTitle className="text-blue-800">Outbound Agent</CardTitle>
                  <CardDescription>HearClear Outbound Leads</CardDescription>
                </CardHeader>
                <CardContent className="p-4 space-y-4">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><span className="text-slate-500">Voice:</span> <span className="font-medium">Priya (Female)</span></div>
                    <div><span className="text-slate-500">WhatsApp:</span> <span className="font-medium">+919605540400</span></div>
                    <div><span className="text-slate-500">Primary Lang:</span> <span className="font-medium">Hindi</span></div>
                    <div><span className="text-slate-500">Max Duration:</span> <span className="font-medium">7 min</span></div>
                    <div><span className="text-slate-500">Speed:</span> <span className="font-medium">0.9x</span></div>
                    <div><span className="text-slate-500">Volume:</span> <span className="font-medium">1.0x</span></div>
                  </div>
                  <Separator />
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-slate-700">Additional Languages:</p>
                    <div className="flex flex-wrap gap-1">
                      {['English', 'Punjabi', 'Kannada', 'Malayalam', 'Telugu', 'Gujarati', 'Tamil'].map(lang => (
                        <Badge key={lang} variant="outline" className="text-xs">{lang}</Badge>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Toggles */}
            <Card>
              <CardHeader>
                <CardTitle>Feature Toggles (Both Agents)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    <span>Enable Follow-ups: ON</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    <span>Enable Appointments: ON</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <span>Front Desk Ambience: OFF</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Prompts Tab */}
          <TabsContent value="prompts" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>Main System Prompt</CardTitle>
                    <CardDescription>Copy this to Agent Info → System Prompt in HuskyVoice</CardDescription>
                  </div>
                  <Button onClick={() => copyToClipboard(mainPrompt, 'main-prompt')}>
                    {copiedSection === 'main-prompt' ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                    Copy Prompt
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-96 rounded border bg-slate-900 p-4">
                  <pre className="text-sm text-green-400 font-mono whitespace-pre-wrap">{mainPrompt}</pre>
                </ScrollArea>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>Closing/Goodbye Prompt</CardTitle>
                    <CardDescription>Optional: Add to closing instructions</CardDescription>
                  </div>
                  <Button variant="outline" onClick={() => copyToClipboard(closingPrompt, 'closing-prompt')}>
                    {copiedSection === 'closing-prompt' ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                    Copy
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <pre className="bg-slate-100 rounded p-4 text-sm font-mono whitespace-pre-wrap">{closingPrompt}</pre>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Knowledge Base Tab */}
          <TabsContent value="knowledge" className="space-y-4">
            {/* Hearing Loss Levels */}
            <Card>
              <CardHeader className="bg-gradient-to-r from-blue-50 to-cyan-50">
                <CardTitle>Hearing Loss Classification (PTA Based)</CardTitle>
                <CardDescription>Pure Tone Audiometry measurement levels</CardDescription>
              </CardHeader>
              <CardContent className="p-4">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-slate-50">
                        <th className="text-left p-3">Level</th>
                        <th className="text-left p-3">dB Range</th>
                        <th className="text-left p-3">Device Recommendation</th>
                        <th className="text-left p-3">Budget Range</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b bg-green-50">
                        <td className="p-3 font-medium">Normal</td>
                        <td className="p-3">0-25 dB</td>
                        <td className="p-3">No hearing aid needed</td>
                        <td className="p-3">-</td>
                      </tr>
                      <tr className="border-b bg-yellow-50">
                        <td className="p-3 font-medium">Mild</td>
                        <td className="p-3">26-40 dB</td>
                        <td className="p-3">Entry/Mid level discreet RIC</td>
                        <td className="p-3">Under ₹40,000</td>
                      </tr>
                      <tr className="border-b bg-orange-50">
                        <td className="p-3 font-medium">Moderate</td>
                        <td className="p-3">41-55 dB</td>
                        <td className="p-3">Mid/Premium RIC, rechargeable</td>
                        <td className="p-3">₹40,000 - ₹1,00,000</td>
                      </tr>
                      <tr className="border-b bg-red-50">
                        <td className="p-3 font-medium">Moderately Severe</td>
                        <td className="p-3">56-70 dB</td>
                        <td className="p-3">Premium stronger RIC/BTE</td>
                        <td className="p-3">₹1,00,000 - ₹2,00,000</td>
                      </tr>
                      <tr className="border-b bg-red-100">
                        <td className="p-3 font-medium">Severe</td>
                        <td className="p-3">71-90 dB</td>
                        <td className="p-3">Power hearing aid</td>
                        <td className="p-3">₹1,50,000 - ₹3,00,000</td>
                      </tr>
                      <tr className="border-b bg-purple-100">
                        <td className="p-3 font-medium">Profound</td>
                        <td className="p-3">90+ dB</td>
                        <td className="p-3">Cochlear implant consultation</td>
                        <td className="p-3">₹3,00,000+</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Hearing Aid Types */}
            <div className="grid grid-cols-2 gap-4">
              <Card>
                <CardHeader className="bg-gradient-to-r from-teal-50 to-green-50">
                  <CardTitle>Hearing Aid Types</CardTitle>
                </CardHeader>
                <CardContent className="p-4 space-y-3">
                  <div className="p-3 border rounded bg-white">
                    <p className="font-semibold">RIC - Receiver in Canal</p>
                    <p className="text-sm text-slate-600">Most common modern hearing aid. Discreet, comfortable.</p>
                  </div>
                  <div className="p-3 border rounded bg-white">
                    <p className="font-semibold">BTE - Behind the Ear</p>
                    <p className="text-sm text-slate-600">More powerful devices for severe loss.</p>
                  </div>
                  <div className="p-3 border rounded bg-white">
                    <p className="font-semibold">ITE - In the Ear</p>
                    <p className="text-sm text-slate-600">Custom molded to fit in outer ear.</p>
                  </div>
                  <div className="p-3 border rounded bg-white">
                    <p className="font-semibold">IIC - Invisible in Canal</p>
                    <p className="text-sm text-slate-600">Very small, completely invisible.</p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50">
                  <CardTitle>Signia Device Families</CardTitle>
                </CardHeader>
                <CardContent className="p-4 space-y-3">
                  <div className="p-3 border rounded bg-white">
                    <p className="font-semibold">Pure Charge & Go</p>
                    <p className="text-sm text-slate-600">Rechargeable, Bluetooth, AI processing</p>
                  </div>
                  <div className="p-3 border rounded bg-white">
                    <p className="font-semibold">Styletto</p>
                    <p className="text-sm text-slate-600">Stylish design for younger users</p>
                  </div>
                  <div className="p-3 border rounded bg-white">
                    <p className="font-semibold">Silk</p>
                    <p className="text-sm text-slate-600">Invisible, custom fit in-ear</p>
                  </div>
                  <div className="p-3 border rounded bg-white">
                    <p className="font-semibold">Motion</p>
                    <p className="text-sm text-slate-600">Powerful BTE for severe loss</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Red Flags */}
            <Card className="border-red-300">
              <CardHeader className="bg-red-50">
                <CardTitle className="text-red-800 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  Red Flags - Refer to ENT First
                </CardTitle>
                <CardDescription>Do NOT recommend hearing aid directly if patient reports:</CardDescription>
              </CardHeader>
              <CardContent className="p-4">
                <div className="grid grid-cols-3 gap-3">
                  {['Active ear discharge', 'Severe ear pain', 'Sudden hearing loss', 'Recent trauma', 'Bleeding from ear', 'Persistent dizziness', 'One-sided sudden drop', 'Recent ear surgery', 'Suspected wax blockage', 'Untreated ear infection'].map(flag => (
                    <div key={flag} className="flex items-center gap-2 p-2 bg-red-50 rounded border border-red-200">
                      <div className="w-2 h-2 rounded-full bg-red-500"></div>
                      <span className="text-sm">{flag}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-4 p-3 bg-yellow-50 rounded border border-yellow-200">
                  <p className="text-sm font-medium">Response Script:</p>
                  <p className="text-sm italic">"Is situation mein pehle ENT ya clinic evaluation zaroori rahega. Main direct machine suggest karne ke bajaye pehle proper check-up recommend karungi."</p>
                </div>
              </CardContent>
            </Card>

            {/* Hearing Loss Types */}
            <Card>
              <CardHeader>
                <CardTitle>Types of Hearing Loss</CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 border rounded bg-blue-50">
                    <p className="font-semibold text-blue-800">Conductive</p>
                    <p className="text-sm text-slate-600 mt-2">Caused by: Ear wax, infection, fluid</p>
                    <p className="text-sm text-green-600 mt-1">Often medically treatable</p>
                  </div>
                  <div className="p-4 border rounded bg-purple-50">
                    <p className="font-semibold text-purple-800">Sensorineural</p>
                    <p className="text-sm text-slate-600 mt-2">Caused by: Aging, noise exposure, genetics</p>
                    <p className="text-sm text-blue-600 mt-1">Usually treated with hearing aids</p>
                  </div>
                  <div className="p-4 border rounded bg-orange-50">
                    <p className="font-semibold text-orange-800">Mixed</p>
                    <p className="text-sm text-slate-600 mt-2">Combination of both types</p>
                    <p className="text-sm text-orange-600 mt-1">Requires comprehensive evaluation</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Qualification Questions */}
            <Card>
              <CardHeader className="bg-gradient-to-r from-green-50 to-teal-50">
                <CardTitle>Lead Qualification Questions</CardTitle>
                <CardDescription>Ask these to understand the patient's condition</CardDescription>
              </CardHeader>
              <CardContent className="p-4">
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { q: 'Sunne mein problem kab se hai?', purpose: 'Duration of hearing loss' },
                    { q: 'Kya dono ears mein problem hai ya ek mein?', purpose: 'Unilateral vs bilateral' },
                    { q: 'Patient ki age kya hai?', purpose: 'Age-appropriate recommendation' },
                    { q: 'Kya hearing test report available hai?', purpose: 'PTA data availability' },
                    { q: 'Koi ear discharge, pain ya infection hai?', purpose: 'Red flag check' },
                    { q: 'TV volume zyada rakhna padta hai?', purpose: 'Symptom severity' },
                    { q: 'Phone pe sunne mein dikkat hoti hai?', purpose: 'Practical hearing difficulty' },
                    { q: 'Noisy jagah par samajhna mushkil hota hai?', purpose: 'Speech-in-noise ability' },
                  ].map((item, i) => (
                    <div key={i} className="p-3 border rounded bg-white">
                      <p className="font-medium text-slate-800">"{item.q}"</p>
                      <p className="text-xs text-slate-500 mt-1">Purpose: {item.purpose}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* API Testing Tab */}
          <TabsContent value="testing" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Terminal className="w-5 h-5" />
                      API Test Commands
                    </CardTitle>
                    <CardDescription>Run these in terminal to verify API connectivity</CardDescription>
                  </div>
                  <Button variant="outline" onClick={() => copyToClipboard(testCommands, 'test-commands')}>
                    {copiedSection === 'test-commands' ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                    Copy All
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-64 rounded border bg-slate-900 p-4">
                  <pre className="text-sm text-green-400 font-mono">{testCommands}</pre>
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Quick Reference */}
            <Card>
              <CardHeader>
                <CardTitle>Quick API Reference</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-slate-50">
                        <th className="text-left p-3">Action</th>
                        <th className="text-left p-3">Method</th>
                        <th className="text-left p-3">Endpoint</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b">
                        <td className="p-3">Get greeting</td>
                        <td className="p-3"><Badge variant="secondary">GET</Badge></td>
                        <td className="p-3 font-mono text-xs">/api/huskyvoice/voice/greeting?phone=&#123;phone&#125;</td>
                      </tr>
                      <tr className="border-b">
                        <td className="p-3">Get pricing</td>
                        <td className="p-3"><Badge>POST</Badge></td>
                        <td className="p-3 font-mono text-xs">/api/huskyvoice/voice/pricing</td>
                      </tr>
                      <tr className="border-b">
                        <td className="p-3">Get clinic</td>
                        <td className="p-3"><Badge>POST</Badge></td>
                        <td className="p-3 font-mono text-xs">/api/huskyvoice/voice/clinic?city=&#123;city&#125;</td>
                      </tr>
                      <tr className="border-b">
                        <td className="p-3">Get discount</td>
                        <td className="p-3"><Badge>POST</Badge></td>
                        <td className="p-3 font-mono text-xs">/api/huskyvoice/voice/discount</td>
                      </tr>
                      <tr className="border-b">
                        <td className="p-3">Book appointment</td>
                        <td className="p-3"><Badge>POST</Badge></td>
                        <td className="p-3 font-mono text-xs">/api/huskyvoice/voice/book?phone=&#123;phone&#125;</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Troubleshoot Tab */}
          <TabsContent value="troubleshoot" className="space-y-4">
            <div className="grid grid-cols-1 gap-4">
              <Card className="border-red-200">
                <CardHeader className="bg-red-50">
                  <CardTitle className="text-red-800 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" />
                    API Not Responding
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <ul className="list-disc list-inside space-y-2 text-sm">
                    <li>Check if URL is correct (should be <code className="bg-slate-100 px-1 rounded">clm.hearclear.in</code>, not preview URL)</li>
                    <li>Verify method (GET vs POST) matches configuration</li>
                    <li>Check Content-Type header is <code className="bg-slate-100 px-1 rounded">application/json</code> for POST requests</li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-yellow-200">
                <CardHeader className="bg-yellow-50">
                  <CardTitle className="text-yellow-800 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" />
                    Priya Not Using API Data
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <ul className="list-disc list-inside space-y-2 text-sm">
                    <li>Ensure "During-Call Workflow" is assigned in Agent Setup</li>
                    <li>Check that workflow is enabled (toggle ON)</li>
                    <li>Verify API returns plain text (not JSON)</li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="border-orange-200">
                <CardHeader className="bg-orange-50">
                  <CardTitle className="text-orange-800 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" />
                    Appointment Not Booking
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <ul className="list-disc list-inside space-y-2 text-sm">
                    <li>Verify phone number format (10 digits, no country code)</li>
                    <li>Check city name is valid and matches our clinic database</li>
                    <li>Ensure date format is correct (YYYY-MM-DD or "kal"/"parso")</li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            {/* Webhook Config */}
            <Card>
              <CardHeader>
                <CardTitle>Webhook Configuration</CardTitle>
                <CardDescription>For receiving call completion events</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Webhook URL:</span>
                    <code className="block mt-1 bg-slate-100 px-2 py-1 rounded text-xs">
                      https://clm.hearclear.in/api/ai-followup/webhooks/voice
                    </code>
                  </div>
                  <div>
                    <span className="text-slate-500">Events:</span>
                    <div className="mt-1 flex gap-2">
                      <Badge variant="outline">call.completed</Badge>
                      <Badge variant="outline">call.failed</Badge>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
